import { Component, OnDestroy, OnInit, Inject } from "@angular/core";
import { AuthenticationService, SpinnerService } from "../_shared/services/services-index";
import { Router } from "@angular/router";
import { catchError, filter, from, of, Subscription, switchMap, tap, timer, take, timeout } from "rxjs";
import { Store } from "@ngrx/store";
import { AppSelectors } from "../_store/selector-types";
import { EntraLoginService } from "../_shared/services/authentication/entra-login.service";
import { environment } from "../../environments/environment";
import { AuthenticationResult, IPublicClientApplication } from "@azure/msal-browser";
import { MSAL_ENTRA_INSTANCE } from "../msal-instances";

@Component({
    selector: 'app-entra-callback',
    template: `
      <div class="text-center my-5">
        <h3>Processing Entra login...</h3>
        <div *ngIf="errorMessage" class="text-red-600 mt-4">
          {{ errorMessage }}
        </div>
        <div *ngIf="isDebugMode" class="mt-4 text-xs bg-gray-100 p-4 rounded-md text-left max-w-lg mx-auto">
          <h4 class="font-bold">Debug Information</h4>
          <div class="mt-2">
            <pre class="whitespace-pre-wrap text-xs">{{ debugInfo | json }}</pre>
          </div>
        </div>
        <div class="mt-4">
          <button *ngIf="errorMessage" class="btn btn-primary" (click)="retryLogin()">Retry Login</button>
          <button class="btn btn-link ml-2" (click)="backToLogin()">Back to Login</button>
        </div>
      </div>
    `
  })
  export class EntraCallbackComponent implements OnInit, OnDestroy {
    errorMessage: string = '';
    isDebugMode = !environment.production;
    debugInfo: any = {
      loginSteps: []
    };
    retryCount = 0;
    maxRetries = 2;
    
    constructor(
                @Inject(MSAL_ENTRA_INSTANCE) private msalEntraInstance: IPublicClientApplication,
                private entraLoginService: EntraLoginService,
                private authService: AuthenticationService,
                private router: Router,
                private spinnerService: SpinnerService,
                private store$: Store) {}
                
    /**
     * Add a step to the debug log with timestamp
     */
    private addDebugStep(step: string): void {
      if (this.isDebugMode) {
        this.debugInfo.loginSteps.push({
          step,
          timestamp: new Date().toISOString()
        });
      }
    }

    subs: Subscription[] = [];

    ngOnInit() {
      this.spinnerService.show();
      console.log('[EntraCallbackComponent] Initializing and processing redirect...');
      this.addDebugStep('Initializing and processing redirect');
      
      // Get accounts at startup to see if we're already logged in
      const accounts = this.msalEntraInstance.getAllAccounts();
      if (accounts.length > 0) {
        console.log('[EntraCallbackComponent] Found existing account at initialization:', accounts[0].name);
        this.addDebugStep(`Found existing account: ${accounts[0].name}`);
        this.debugInfo.existingAccount = accounts[0].name;
        // Potentially could proceed directly to token exchange here if we have an account,
        // but we'll let the redirect handling process complete to be safe
      }
      
      // Add timeout to bail out if token exchange takes too long
      const timeoutSub = timer(30000).subscribe(() => {
        if (!this.errorMessage) {
          this.errorMessage = 'Login process timed out. Please try again.';
          this.debugInfo.timeoutError = true;
          this.addDebugStep('Login process timed out after 30 seconds');
          this.spinnerService.hide();
        }
      });
      this.subs.push(timeoutSub);
      
      // Listen for MSAL login events in parallel with our direct handling
      // This provides a fallback mechanism in case the direct redirect handling fails
      const loginSub = this.entraLoginService.onLoginSuccess$().subscribe(event => {
        console.log('[EntraCallbackComponent] Detected login success event:', event.eventType);
        this.addDebugStep(`Detected login success event: ${event.eventType}`);
        this.debugInfo.loginSuccessEvent = true;
        this.debugInfo.loginSuccessEventType = event.eventType;
        this.debugInfo.loginSuccessEventTimestamp = new Date().toISOString();
        
        // Check if we have an account after the success event
        const accounts = this.msalEntraInstance.getAllAccounts();
        if (accounts.length > 0) {
          console.log('[EntraCallbackComponent] Account available after event detection:', accounts[0].name);
          this.addDebugStep(`Account available after event: ${accounts[0].name}`);
          
          // If we're still waiting (no error yet) but have an account, we could proceed with token exchange
          // This might help if the direct redirect handling is stuck but we received a success event
          if (!this.errorMessage && !this.debugInfo.authResultReceived && !this.debugInfo.tokenExchangedSuccessfully) {
            this.addDebugStep('Proceeding with token exchange based on success event');
            // We'll let the redirect handling continue its process, but note this as a potential path
            // You could implement a more aggressive approach here if needed
          }
        }
      });
      this.subs.push(loginSub);
      
      // Handle the MSAL redirect directly in the callback component
      this.processRedirect();
    }
    
    /**
     * Process the MSAL redirect response using our improved method
     */
    private processRedirect(): void {
      this.addDebugStep('Starting redirect handling with enhanced handleRedirectObservable');
      console.log('[EntraCallbackComponent] Starting enhanced redirect handling...');
      
      // Check if we already have accounts before even trying the redirect handling
      const existingAccounts = this.msalEntraInstance.getAllAccounts();
      if (existingAccounts.length > 0) {
        this.addDebugStep(`Found existing account before redirect handling: ${existingAccounts[0].name}`);
        console.log('[EntraCallbackComponent] Already have account before redirect handling:', existingAccounts[0].name);
      }
      
      // Create a subscription to handle the redirect using our improved method
      const sub = this.entraLoginService.handleRedirectObservable()
        .pipe(
          tap(() => {
            this.debugInfo.redirectHandlingStarted = true;
            this.debugInfo.timestamp = new Date().toISOString();
            this.addDebugStep('Redirect handling started');
          }),
          catchError(error => {
            // Handle error in the redirect promise
            console.error('[EntraCallbackComponent] Error in redirect handling:', error);
            this.addDebugStep(`Error in redirect handling: ${error.message}`);
            
            // Check if we have accounts despite the error
            const accounts = this.msalEntraInstance.getAllAccounts();
            if (accounts.length > 0) {
              this.addDebugStep(`Found account despite redirect error: ${accounts[0].name}`);
              console.log('[EntraCallbackComponent] Found account despite redirect error:', accounts[0].name);
              
              // Try to acquire token silently for this account
              return from(this.msalEntraInstance.acquireTokenSilent({
                scopes: ['openid', 'profile', 'email'],
                account: accounts[0]
              })).pipe(
                catchError(silentError => {
                  console.error('[EntraCallbackComponent] Silent token acquisition failed:', silentError);
                  this.addDebugStep(`Silent token acquisition failed: ${silentError.message}`);
                  throw error; // throw original error if silent acquisition fails
                })
              );
            }
            
            // Retry logic for redirect handling
            if (this.retryCount < this.maxRetries) {
              this.retryCount++;
              this.addDebugStep(`Retrying redirect handling (attempt ${this.retryCount} of ${this.maxRetries})`);
              // Wait a bit and retry
              setTimeout(() => this.processRedirect(), 1000);
              return of(null);
            }
            
            this.errorMessage = `MSAL redirect handling failed: ${error.message}`;
            this.spinnerService.hide();
            return of(null);
          }),
          filter(authResult => !!authResult), // Filter out null results from error handling
          tap(authResult => {
            console.log('[EntraCallbackComponent] Auth result received', authResult);
            this.addDebugStep('Auth result received from MSAL');
            
            if (this.isDebugMode) {
              this.debugInfo.authResultReceived = true;
              this.debugInfo.authResultTimestamp = new Date().toISOString();
              this.debugInfo.userAccountName = authResult.account?.name;
              this.debugInfo.tokenType = authResult.tokenType;
              this.debugInfo.hasIdToken = !!authResult.idToken;
            }
          }),
        switchMap(authResult => {
          if (!authResult.idToken) {
            console.error('[EntraCallbackComponent] No ID token in auth result');
            this.addDebugStep('No ID token in auth result');
            if (this.isDebugMode) {
              this.debugInfo.noIdToken = true;
            }
            throw new Error('No ID token received from Entra ID');
          }
          
          this.addDebugStep('ID token received, exchanging for app token');
          return this.authService.exchangeEntraToken(authResult.idToken).pipe(
            catchError(error => {
              console.error('[EntraCallbackComponent] Token exchange failed', error);
              this.addDebugStep(`Token exchange failed: ${error.message || 'Unknown error'}`);
              if (this.isDebugMode) {
                this.debugInfo.tokenExchangeError = error.message || 'Unknown error during token exchange';
                this.debugInfo.tokenExchangeErrorStatus = error.status;
                this.debugInfo.tokenExchangeErrorTimestamp = new Date().toISOString();
              }
              throw error;
            })
          );
        }),
        // unifiedTokenResponse: { accessToken, refreshToken, expiresIn }
        tap(unifiedTokenResponse => {
          console.log('[EntraCallbackComponent] Token exchanged successfully', unifiedTokenResponse);
          if (this.isDebugMode) {
            this.debugInfo.tokenExchangedSuccessfully = true;
            this.debugInfo.tokenExchangeTimestamp = new Date().toISOString();
            this.debugInfo.expiresIn = unifiedTokenResponse.expiresIn;
          }
        }),
        switchMap(unifiedTokenResponse => {
          return this.authService.loginCustomToken(unifiedTokenResponse.accessToken, unifiedTokenResponse.refreshToken).pipe(
            catchError(error => {
              console.error('[EntraCallbackComponent] Custom token login failed', error);
              if (this.isDebugMode) {
                this.debugInfo.customTokenLoginError = error.message || 'Unknown error during custom token login';
                this.debugInfo.customTokenLoginErrorTimestamp = new Date().toISOString();
              }
              throw error;
            }),
            // Add a small delay to ensure Redux has time to process the action
            tap(() => {
              console.log('[EntraCallbackComponent] Custom token login completed, waiting for store update');
              this.addDebugStep('Custom token login completed, waiting for store update');
            })
          );
        }),
        // Use a retry mechanism with delay to check for the user in the store
        switchMap(() => {
          console.log('[EntraCallbackComponent] Checking for user in store...');
          this.addDebugStep('Checking for user in store');
          
          // Create a stream that checks for the user multiple times with a delay between attempts
          return timer(0, 500).pipe( // Check immediately, then every 500ms
            tap(counter => {
              console.log(`[EntraCallbackComponent] User store check attempt ${counter + 1}`);
              this.addDebugStep(`User store check attempt ${counter + 1}`);
              if (this.isDebugMode) {
                this.debugInfo.userStoreCheckAttempt = counter + 1;
              }
            }),
            switchMap(() => this.store$.select(AppSelectors.selectLoggedInUser)),
            tap(user => {
              console.log('[EntraCallbackComponent] User in store check:', user ? 'found' : 'not found yet');
              if (user) {
                this.addDebugStep('User found in store');
                if (this.isDebugMode) {
                  this.debugInfo.userFoundInStore = true;
                  this.debugInfo.userFoundTimestamp = new Date().toISOString();
                }
              }
            }),
            // Take the first non-null user or stop after 10 attempts (5 seconds)
            filter(user => !!user),
            take(1),
            timeout(5000), // Fail after 5 seconds of trying
            catchError(error => {
              console.error('[EntraCallbackComponent] Timed out waiting for user in store', error);
              this.addDebugStep('Timed out waiting for user in store');
              if (this.isDebugMode) {
                this.debugInfo.userStoreTimeoutError = true;
              }
              throw new Error('Login succeeded but user data was not available in store within the timeout period');
            })
          );
        }),
        // Now we should definitely have the user
        tap(user => {
          if (!user) {
            console.error('[EntraCallbackComponent] No user after login and retries');
            this.addDebugStep('No user after login and retries');
            if (this.isDebugMode) {
              this.debugInfo.noUserAfterLogin = true;
            }
            throw new Error('Login succeeded but no user data was retrieved after retries');
          }
        }),
        tap(user => {
          console.log('[EntraCallbackComponent] Login completed successfully', user);
          if (this.isDebugMode) {
            this.debugInfo.loginSuccessful = true;
            this.debugInfo.loginTimestamp = new Date().toISOString();
            this.debugInfo.userName = user.userName;
            this.debugInfo.userRole = user.role;
          }
        }),
        tap(() => {
          this.spinnerService.hide();
          this.router.navigate(['/']);
        }),
        catchError(err => {
          console.error('[EntraCallbackComponent] Login process error', err);
          this.spinnerService.hide();
          this.addDebugStep(`Login process failed: ${err.message || 'Unknown error'}`);
          
          // Create a more informative error message
          let errorDetails = '';
          if (err.errorCode) {
            errorDetails = `[${err.errorCode}] `;
          }
          errorDetails += err.message || 'Unknown error';
          
          // Provide guidance based on error type
          if (err.message?.includes('interaction_in_progress')) {
            errorDetails += '. Try clearing your browser cache and cookies.';
          } else if (err.message?.includes('token')) {
            errorDetails += '. There may be an issue with the authentication configuration.';
          }
          
          this.errorMessage = `Login failed: ${errorDetails}`;
          
          if (this.isDebugMode) {
            this.debugInfo.loginError = err.message || 'Unknown error';
            this.debugInfo.errorCode = err.errorCode;
            this.debugInfo.loginErrorStack = err.stack;
            this.debugInfo.errorTimestamp = new Date().toISOString();
            this.debugInfo.msalInstance = this.msalEntraInstance ? 'Available' : 'Not available';
            // Add potentially helpful debugging info
            try {
              this.debugInfo.accounts = this.msalEntraInstance?.getAllAccounts()?.length || 0;
            } catch (e) {
              this.debugInfo.accountsError = 'Failed to get accounts';
            }
          }
          
          return of(null); // Don't navigate away, show the error on this page
        })
      ).subscribe();

      this.subs.push(sub);
    }

    ngOnDestroy() {
      this.subs.forEach(sub => sub.unsubscribe());
      this.spinnerService.hide();
    }
    
    /**
     * Retry the login process
     */
    retryLogin() {
      this.errorMessage = '';
      this.spinnerService.show();
      this.router.navigate(['/login']).then(() => {
        this.entraLoginService.loginRedirect();
      });
    }
    
    /**
     * Navigate back to the login page
     */
    backToLogin() {
      this.router.navigate(['/login']);
    }
  }
